import { useScrollReveal } from "@/hooks/useScrollReveal";
import houseRulesLandscapeImg from "@/assets/house-rules-landscape.png";

const rules = [
  {
    icon: "ri-footprint-line",
    title: "Pets Welcome",
    body: "Pets are welcome, but not on beds, couches, or furniture. Owners are responsible for their pets at all times and must clean up after them.",
  },
  {
    icon: "ri-car-line",
    title: "Farm Access",
    body: "Guests may not drive any vehicles on the farm. Walking and cycling are warmly encouraged.",
  },
  {
    icon: "ri-door-open-line",
    title: "Gates",
    body: "If you find a closed gate, please leave it closed. If you open a gate, ensure it is properly closed behind you to prevent livestock from escaping.",
  },
  {
    icon: "ri-leaf-line",
    title: "Respect the Farm",
    body: "Please respect the farmhouse, facilities, and surrounding environment. Do not disturb livestock or wildlife.",
  },
  {
    icon: "ri-moon-line",
    title: "Noise Levels",
    body: "To help keep the farm and its natural surroundings peaceful, please keep noise to a minimum, especially in the late evening.",
  },
  {
    icon: "ri-fire-line",
    title: "Smoking",
    body: "Smoking is only permitted in designated outdoor areas. Please dispose of cigarette butts responsibly.",
  },
  {
    icon: "ri-shield-line",
    title: "Safety",
    body: "Guests enter and explore the farm at their own risk. Please be mindful of uneven terrain, animals, and farm equipment.\u00a0",
  },
  {
    icon: "ri-sparkling-line",
    title: "Cleanliness",
    body: "As this is a historic farmhouse, we kindly ask guests to treat the farmhouse with care and leave it in a neat condition. Cleaning services are provided if prior arrangements have been made.",
  },
  {
    icon: "ri-group-line",
    title: "Visitors",
    body: "Only registered guests are allowed on the property unless prior arrangements have been made with the owner.",
  },
];

export default function HouseRules() {
  const sectionRef = useScrollReveal();

  return (
    <section
      id="house-rules"
      ref={sectionRef}
      className="overflow-hidden"
      style={{ backgroundColor: "#F0EBE0" }}
    >
      {/* Full-width image pause */}
      <div className="relative w-full overflow-hidden" style={{ height: "55vh" }}>
        <img
          src={houseRulesLandscapeImg}
          alt="Horses crossing the wetlands at Rietkuil Farm"
          className="w-full h-full object-cover object-center img-washed"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/40"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-16 text-center px-6">
          <p className="fade-in text-base tracking-[0.1em] uppercase font-sans font-medium mb-4" style={{ color: "rgba(255,255,255,0.9)" }}>
            Before You Arrive
          </p>
          <h2
            className="fade-in delay-100 font-serif font-medium leading-tight"
            style={{ fontSize: "clamp(2.8rem, 5vw, 5rem)", color: "#F5F0E8", textShadow: "0 1px 2px rgba(0,0,0,0.25), 0 0 40px rgba(0,0,0,0.15)" }}
          >
            House Rules
          </h2>
        </div>
      </div>

      {/* Intro */}
      <div className="max-w-2xl mx-auto px-8 md:px-12 pt-24 pb-16 text-center">
        <p className="fade-in font-sans font-light leading-loose text-sm" style={{ color: "#8C8478" }}>
          To ensure a safe, comfortable, and enjoyable stay for all guests, please follow the guidelines below. These simple rules help us protect the farm, care for the animals, and preserve the peaceful atmosphere that makes Rietkuil special. In addition, an indemnity form will be sent to guests once their booking has been confirmed.
        </p>
      </div>

      {/* Rules grid */}
      <div className="max-w-6xl mx-auto px-8 md:px-16 pb-28">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14">
          {rules.map((rule, i) => (
            <div
              key={rule.title}
              className="fade-in"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              {/* Icon */}
              <div className="w-9 h-9 flex items-center justify-center mb-5">
                <i className={`${rule.icon} text-xl`} style={{ color: "#A8B09E" }}></i>
              </div>
              {/* Divider */}
              <div className="w-6 h-px mb-5" style={{ backgroundColor: "#C8C0B4" }}></div>
              <h3
                className="font-serif font-medium mb-3 leading-snug"
                style={{ fontSize: "1.25rem", color: "#3C3530" }}
              >
                {rule.title}
              </h3>
              <p
                className="font-sans font-light text-sm leading-loose"
                style={{ color: "#8C8478" }}
              >
                {rule.body}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div
          className="fade-in mt-20 pt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          style={{ borderTop: "1px solid #D8D0C4" }}
        >
          <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
            <i className="ri-information-line text-base" style={{ color: "#A8B09E" }}></i>
          </div>
          <p className="font-sans font-light text-xs leading-loose" style={{ color: "#A8A090" }}>
            By booking your stay at Rietkuil Farmhouse, you agree to abide by these house rules. If you have any questions before your arrival, please don&apos;t hesitate to get in touch — we&apos;re happy to help.
          </p>
        </div>
      </div>
    </section>
  );
}
